import React, {useContext, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {
    IButtonProps,
    IDataInfoProps,
    IFilterProps,
    ITableRowProps,
    ModalProvider,
    TemplateQuestions,
    TFilterValue,
    Toggle,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {
    Announcement,
    CatalogQuestionsWithCategory,
    Languages,
    QuestionsStore,
    QuestionsTab,
    SingleAdHocQuestion,
    SingleCatalogQuestion,
} from './questions-store';
import {
    ITemplateQuestionsAction,
    ITemplateQuestionsRowProps,
    ITemplateQuestionsTableProps,
} from '../../../patterns/templates/questions/questions';
import {WebappBase} from '../dashboard/WebappBase';
import {AdHocQuestionModal} from './AdHocQuestionModal';
import {formatToDayMonthAndYear, isDateInFuture} from '../base/dateUtils';
import {UserStore} from '../user/user-store';
import {QuestionsPageHelpModal} from './QuestionsPageHelpModal';
import {CatalogQuestionModal} from './CatalogQuestionModal';
import {CustomCategoryModal} from '../categories/CustomCategoryModal';
import {getCategoryName, isCustomCategory} from '../categories/CategoriesUtil';
import {DisableCategoryModal} from '../categories/DisableCategoryModal';
import Button from '../../../patterns/components/button/button';
import {DisableQuestionModal} from '../categories/DisableQuestionModal';
import {AnnouncementModal} from './AnnouncementModal';
import {SessionStore} from '../user/session-store';


export const Questions = observer((): JSX.Element => {
    const store = useContext(StoreContext).questionStore;
    const userStore = useContext(StoreContext).userStore;
    const sessionStore = useContext(StoreContext).sessionStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    const questionsPageHelpModal: ITemplateQuestionsAction = {
        options: [
            {
                text: '',
                icon: 'info',
                color: 'white',
                onClick: (): void => store.openQuestionsPageHelpModal(),
            },
        ],
    };

    return (
        <WebappBase
            store={store}
            title={store.label('questions.title')}
            renderChildren={ (): React.ReactNode => (
                <React.Fragment>
                    <ModalProvider
                        isOpen={store.variables.openModal !== undefined}
                        onToggle={(): void => store.closeModal()}>
                        {store.variables.openModal === 'add-adhoc-question' && <AdHocQuestionModal/>}
                        {store.variables.openModal === 'edit-adhoc-question' && <AdHocQuestionModal/>}
                        {store.variables.openModal === 'add-catalog-question' && <CatalogQuestionModal/>}
                        {store.variables.openModal === 'edit-catalog-question' && <CatalogQuestionModal/>}
                        {store.variables.openModal === 'add-custom-category' && <CustomCategoryModal/>}
                        {store.variables.openModal === 'edit-custom-category' && <CustomCategoryModal/>}
                        {store.variables.openModal === 'add-announcement' && <AnnouncementModal/>}
                        {store.variables.openModal === 'edit-announcement' && <AnnouncementModal/>}
                        {store.variables.openModal === 'disable-category' && <DisableCategoryModal/>}
                        {store.variables.openModal === 'disable-question' && <DisableQuestionModal/>}
                        {store.variables.openModal === 'question-help' && <QuestionsPageHelpModal/>}
                    </ModalProvider>
                    <TemplateQuestions
                        title={store.label('questions.title')}
                        filter={
                            userStore.payload?.isAdmin === true ||
                            userStore.payload?.isManagerOrPartner === true
                                ? questionsFilterProps(store)
                                : undefined
                        }
                        rightToolbarItems={{
                            actions: questionsPageHelpModal,
                            buttons: (() => {
                                const isAdmin = userStore.payload?.isAdmin;
                                const isManagerOrPartner = userStore.payload?.isManagerOrPartner;
                                const tab = store.variables.openTab;

                                if (isAdmin) {
                                    if (tab === 'ad-hoc-questions') {
                                        return [addAdHocQuestionProps(store)];
                                    }
                                    if (tab === 'announcements') {
                                        return [addAnnouncementProps(store, sessionStore)];
                                    }

                                    return [addCustomQuestionCategoryProps(store), addCatalogQuestionProps(store)];
                                }

                                if (isManagerOrPartner && tab === 'ad-hoc-questions') {
                                    return [addAdHocQuestionProps(store)];
                                }

                                return [];
                            })(),
                        }}
                        table={
                            store.variables.openTab === 'ad-hoc-questions'
                                ? adHocQuestionsTableProps(store, userStore)
                                : store.variables.openTab === 'announcements'
                                    ? announcementsTableProps(store, userStore)
                                    : catalogQuestionsTableProps(store, userStore)
                        }
                        onboardingTable={
                            store.variables.openTab === 'ad-hoc-questions' || store.variables.openTab === 'announcements'
                                ? undefined
                                : onboardingQuestionTableProps(store, userStore)
                        }
                    />
                </React.Fragment>
            )
            }/>
    );
});

const getQuestionUsageInBusinessUnits = (
    userStore: UserStore,
    store: QuestionsStore,
    question: SingleCatalogQuestion,
): string => {
    const businessUnits = userStore.payload!.businessUnits;

    const allDisabledBusinessUnits = store.payload?.allQuestionsWithCategories.find((category) =>
        category.allCatalogQuestions.some((q) => q.id === question.id)) ?.disabledBusinessUnits || [];

    const allBusinessUnits = businessUnits.filter(
        (unit) => !allDisabledBusinessUnits.includes(unit.id));

    const allBusinessUnitsCount = allBusinessUnits.length;

    const catalogQuestionCategory = store.payload?.allQuestionsWithCategories.find(
        (c) => c.categoryId === question.categoryId);

    const allQuestionsInCategory = catalogQuestionCategory?.allCatalogQuestions || [];

    const disabledBusinessUnitsCount =
        allQuestionsInCategory.find((q) => q.id === question.id)?.disabledBusinessUnits.filter(
            (id) => allBusinessUnits.some((unit) => unit.id === id)).length || 0;

    const activeCount = allBusinessUnitsCount - disabledBusinessUnitsCount;

    return `${activeCount} / ${allBusinessUnitsCount}`;
};

const processQuestions = (
    questionsData: CatalogQuestionsWithCategory[],
    store: QuestionsStore,
    userStore: UserStore,
): ITemplateQuestionsRowProps[] => {
    const questions: ITemplateQuestionsRowProps[] = [];

    const sortedQuestions = questionsData.slice().sort((a, b) => {
        const dateA = new Date(a.createdTs);
        const dateB = new Date(b.createdTs);

        return dateA.getTime() - dateB.getTime();
    });

    sortedQuestions.forEach((catalogQuestion) => {
        const categoryId = catalogQuestion.categoryId;
        const catalogQuestions = catalogQuestion.allCatalogQuestions.map((singleQuestion: SingleCatalogQuestion): ITemplateQuestionsRowProps => ({
            id: singleQuestion.id,
            cells: [
                {
                    content: textInUserLanguage(userStore, singleQuestion.questionLanguages),
                },
                {
                    content: userStore.payload?.isAdmin === true ? <Toggle
                        input={{
                            onChange: (event): void => {
                                store.onSendingActiveToggle(singleQuestion.id, event.target.checked);
                            },
                            checked: singleQuestion.requestedIsActive,
                        }}
                        id={singleQuestion.id}
                        isPending={singleQuestion.isActive !== singleQuestion.requestedIsActive}
                    />
                        : undefined,
                },
                {
                    content: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled ?
                        <Button
                            style="link"
                            block={false}
                            color="white"
                            text={getQuestionUsageInBusinessUnits(userStore, store, singleQuestion)}
                            onClick={() => store.openDisableQuestionForBusinessUnitModal(singleQuestion)}
                        /> : undefined,
                },
            ],
            actions: singleQuestion.isCustomCatalogQuestion && userStore.payload?.isAdmin === true ? {
                trigger: {
                    text: 'More',
                },
                options: [
                    {
                        text: store.label('questions.edit-question-button'),
                        iconLeft: 'settings',
                        onClick: (): void => store.openEditCatalogQuestionModal(singleQuestion, categoryId),
                    },
                    {
                        text: store.label('questions.delete-question-button'),
                        iconLeft: 'delete',
                        disabled: singleQuestion.isAsked,
                        onClick: (): void => store.deleteCatalogQuestion(singleQuestion),
                    },
                ],
            } : undefined,
            isTextGreyedOut: !singleQuestion.requestedIsActive,
        }));

        const editableCategory = store.payload?.questionCategories.find((item) => item.id === catalogQuestion.categoryId);
        const catalogActions: ITemplateQuestionsAction | undefined = editableCategory ? {
            trigger: {
                text: 'More',
            },
            options: [
                {
                    text: store.label('questions.edit-question-button'),
                    iconLeft: 'settings',
                    onClick: (): void => {
                        store.openEditCustomCategoryModal(editableCategory);
                    },
                },
                {
                    text: store.label('questions.delete-question-button'),
                    iconLeft: 'settings',
                    disabled: catalogQuestion.allCatalogQuestions
                        .filter((question: SingleCatalogQuestion) => question.categoryId === editableCategory.id)
                        .some((question: SingleCatalogQuestion) => question.isAsked),
                    onClick: (): void => {
                        store.deleteCustomCategory(editableCategory.id);
                    },
                },
            ],
        } : undefined;

        const getCategoryUsageInBusinessUnits = (catId: string): string => {
            const businessUnits = userStore?.payload?.businessUnits;
            const categories = store.payload?.allQuestionsWithCategories;

            if (!businessUnits) {
                return '';
            }

            const allBusinessUnitsCount = businessUnits.length;

            const selectedCategory = categories?.find((category) => category.categoryId === catId);

            const disabledBusinessUnitsCount = selectedCategory?.disabledBusinessUnits?.length || 0;
            const activeCount = allBusinessUnitsCount - disabledBusinessUnitsCount;

            return `${activeCount} / ${allBusinessUnitsCount}`;
        };


        questions.push({
            cells: [
                {
                    content: <div> { getCategoryName(userStore.payload!.language, categoryId, store.categories) }
                        <span className="question__text-bold-green">{ ' ' + catalogQuestions.filter((q: ITableRowProps) => q.isTextGreyedOut === false).length}</span>
                        { '/' + catalogQuestions.length }
                    </div>,
                },
                {
                    content: userStore.payload?.isAdmin === true ? <Toggle
                        input={{
                            onChange: (event): void => {
                                store.onSendingActiveCategoryToggle(categoryId, event.target.checked);
                            },
                            checked: catalogQuestion.allCatalogQuestions.some((question: SingleCatalogQuestion) => question.requestedIsActive),
                        }}
                        id={categoryId}
                        isPending={catalogQuestion.allCatalogQuestions.some((question: SingleCatalogQuestion) => question.requestedIsActive !== question.isActive)}
                    /> : undefined,
                },
                {
                    content: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled ?
                        <Button style={'link'} block={false} color={'white'} text={getCategoryUsageInBusinessUnits(categoryId)} isTextBold={true}
                            onClick={(): void => store.openDisableCategoryForBusinessUnitModal(categoryId)}/> : undefined,
                },
            ],
            subRows: catalogQuestions,
            actions: userStore.payload?.isAdmin === true && isCustomCategory(categoryId) ? catalogActions : undefined,
            isTextBold: true,
        });
    });

    return questions;
};

const catalogQuestionsTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {
    const questions = processQuestions(store.payload!.allQuestionsWithCategories.filter((category) => !category.isOnboarding), store, userStore);

    return ({
        columns: [
            {
                header: store.label('questions.catalog-questions.sub-header.questions'),
                width: userStore.payload?.isBusinessUnitDisablingEnabled ? '60%' : '90%',
            },
            {
                header: userStore.payload?.isAdmin === true ? store.label('questions.catalog-questions.sub-header.toggle') : '',
                width: '10%',
            },
            {
                header: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled? store.label('questions.catalog-questions.sub-header.team-usage') : '',
                width: '30%',
            },
        ],
        rows: questions,
    });
};

const onboardingQuestionTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {
    const onboardingQuestions = processQuestions(store.payload!.allQuestionsWithCategories.filter((category) => category.isOnboarding), store, userStore);

    return ({
        columns: [
            {
                header: store.label('questions.catalog-questions.sub-header.onboarding-questions'),
                width: userStore.payload?.isBusinessUnitDisablingEnabled ? '60%' : '90%',
            },
            {
                header: userStore.payload?.isAdmin === true ? store.label('questions.catalog-questions.sub-header.toggle') : '',
                width: '10%',
            },
            {
                header: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled? store.label('questions.catalog-questions.sub-header.team-usage') : '',
                width: '30%',
            },
        ],
        rows: onboardingQuestions,
    });
};

const adHocQuestionsTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {

    const businessUnitIds = userStore?.payload?.businessUnits
        ? userStore.payload.businessUnits.map((unit) => unit.id)
        : [userStore.payload?.businessUnitId];

    const rows: ITemplateQuestionsRowProps[] = store.payload!.allAdHocQuestions.filter((it) => businessUnitIds.includes(it.businessUnitId)).map((singleQuestion: SingleAdHocQuestion): ITemplateQuestionsRowProps => {

        const adHocQuestionLanguage = textInUserLanguage(userStore, singleQuestion.questionLanguages);

        const editOrDeleteActions = {
            trigger: {
                text: 'More',
            },
            options: [
                {
                    text: store.label('questions.edit-question-button'),
                    iconLeft: 'settings',
                    onClick: (): void => store.openEditAdHocQuestionModal(singleQuestion),
                },
                {
                    text: store.label('questions.delete-question-button'),
                    iconLeft: 'delete',
                    onClick: (): void => store.deleteAdHocQuestion(singleQuestion),
                },
            ],
        };

        return ({
            id: singleQuestion.id,
            cells: [
                {
                    content: adHocQuestionLanguage,
                    comparableValue: adHocQuestionLanguage,
                },
                {
                    content: singleQuestion.businessUnitName,
                    comparableValue: singleQuestion.businessUnitName,
                },
                {
                    content: store.label(`questions.ad-hoc.employee-segment.${singleQuestion.employeeSegment}`),
                    comparableValue: store.label(`questions.ad-hoc.employee-segment.${singleQuestion.employeeSegment}`),
                },
                {
                    content: store.payload!.employeeLocations.length > 1 ? store.label(`common.location.${singleQuestion.employeeLocation}`) : undefined,
                    comparableValue: store.payload!.employeeLocations.length > 1 ? store.label(`common.location.${singleQuestion.employeeLocation}`) : undefined,
                },
                {
                    content: formatToDayMonthAndYear(singleQuestion.sendDate),
                    comparableValue: singleQuestion.sendDate,
                },
            ],
            actions: isDateInFuture(new Date(singleQuestion.sendDate)) ? editOrDeleteActions : undefined,
        });
    });

    return ({
        columns: [
            {
                header: store.label('questions.ad-hoc.sub-header.title'),
                width: '30%',
                isSortable: true,
            },
            {
                header: store.label('questions.ad-hoc.sub-header.target-business-unit'),
                width: '20%',
                isSortable: true,
            },
            {
                header: store.label('questions.ad-hoc.sub-header.employee-segment'),
                width: '20%',
                isSortable: true,
            },
            {
                header: store.payload!.employeeLocations.length > 1 ? store.label('questions.ad-hoc.sub-header.employee-location') : '',
                width: '15%',
                isSortable: store.payload!.employeeLocations.length > 1 ? true : undefined,
            },
            {
                header: store.label('questions.ad-hoc.sub-header.send-date'),
                width: '15%',
                isSortable: true,
            },
        ],
        rows: rows.length > 0 ? rows : {
            type: 'info',
            text: store.label('questions.ad-hoc.no-data'),
        } as IDataInfoProps,
    });
};

const announcementsTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {
    const rows: ITemplateQuestionsRowProps[] = store.payload!.allAnnouncements.map((announcement: Announcement): ITemplateQuestionsRowProps => {

        const announcementLanguage = textInUserLanguage(userStore, announcement.languages);

        const editOrDeleteActions = {
            trigger: {
                text: 'More',
            },
            options: [
                {
                    text: store.label('questions.edit-question-button'),
                    iconLeft: 'settings',
                    onClick: (): void => store.openEditAnnouncementModal(announcement),
                },
                {
                    text: store.label('questions.delete-question-button'),
                    iconLeft: 'delete',
                    onClick: (): void => {
                        if (announcement.id) {
                            store.deleteAnnouncement(announcement.id);
                        } else {
                            console.warn('Attempted to delete an announcement without ID');
                        }
                    },
                },
            ],
        };

        return ({
            id: announcement.id,
            cells: [
                {
                    content: announcementLanguage,
                    comparableValue: announcementLanguage,
                },
                {
                    content: announcement.targetRoles
                        .map((role) => store.label(`common.filter.segment.${role}`))
                        .join(', '),
                    comparableValue: announcement.targetRoles
                        .map((role) => store.label(`common.filter.segment.${role}`))
                        .join(', '),
                },
                {
                    content: formatToDayMonthAndYear(announcement.startAt),
                    comparableValue: announcement.startAt,
                },
                {
                    content: formatToDayMonthAndYear(announcement.endAt),
                    comparableValue: announcement.endAt,
                },
            ],
            actions: isDateInFuture(new Date(announcement.endAt)) ? editOrDeleteActions : undefined,
        });
    });

    return ({
        columns: [
            {
                header: store.label('questions.announcement.title'),
                width: '30%',
                isSortable: true,
            },
            {
                header: store.label('questions.announcement.employee-group'),
                width: '20%',
                isSortable: true,
            },
            {
                header: store.label('questions.announcement.start-date'),
                width: '15%',
                isSortable: true,
            },
            {
                header: store.label('questions.announcement.end-date'),
                width: '15%',
                isSortable: true,
            },
        ],
        rows: rows.length > 0 ? rows : {
            type: 'info',
            text: store.label('questions.announcement.no-announcements'),
        } as IDataInfoProps,
    });
};

const questionsFilterProps = (store:QuestionsStore): IFilterProps => ({
    label: 'Filter label',
    onChange: (value: TFilterValue): void => store.onQuestionsTabChange(value as QuestionsTab),
    value: store.variables.openTab,

    items: [
        {
            id: 'filter-1',
            name: 'filter',
            label: store.label('questions.filter.catalog-questions'),
            value: 'catalog-questions',
        },
        {
            id: 'filter-2',
            name: 'filter',
            label: store.label('questions.filter.ad-hoc-questions'),
            value: 'ad-hoc-questions',
        },
        {
            id: 'filter-3',
            name: 'filter',
            label: store.label('questions.announcement.filter.announcements'),
            value: 'announcements',
        },
    ],
});

const addAdHocQuestionProps = (store: QuestionsStore): IButtonProps => ({
    text: store.label('questions.ad-hoc.add-question-button'),
    onClick: (): void => store.openAddAdHocQuestionModal(),
});

const addAnnouncementProps = (store: QuestionsStore, sessionStore: SessionStore): IButtonProps => {
    const label =
        sessionStore.state === 'admin-session'
            ? store.label('questions.announcement.add-button.app')
            : store.label('questions.announcement.add-button.company');

    return {
        text: label,
        onClick: (): void => store.openAddAnnouncementModal(),
    };
};

const addCatalogQuestionProps = (store: QuestionsStore): IButtonProps => ({
    text: store.label('questions.catalog-question.add-question-button'),
    onClick: (): void => store.openAddCatalogQuestionModal(),
});

const addCustomQuestionCategoryProps = (store: QuestionsStore): IButtonProps => ({
    text: store.label('categories.custom-category.add-category-button'),
    onClick: (): void => store.openAddCustomQuestionCategoryModal(),
});

export const textInUserLanguage = (userStore: UserStore, languages: Languages): string => {
    switch (userStore.payload?.language) {
        case 'et':
            return languages.textEstonian!;
        case 'ru':
            return languages.textRussian!;
        case 'fi':
            return languages.textFinnish!;
        case 'lt':
            return languages.textLithuanian!;
        case 'lv':
            return languages.textLatvian!;
        case 'sv':
            return languages.textSwedish!;
        case 'pl':
            return languages.textPolish!;
        case 'ja':
            return languages.textJapanese!;
        case 'vi':
            return languages.textVietnamese!;
        default:
            return languages.textEnglish!;
    }
};
